import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";

import { PublicHeader } from "@/components/layout/public-header";
import { useAuth } from "@/lib/auth/AuthContext";
import { LegalLinks } from "@/components/legal/legal-links";

/**
 * The frame both legal pages share: public, outside the sidebar shell, for the
 * reason /duk is — the person reading may have no account, and the point of
 * the page is to be read *before* making one.
 *
 * `draft` puts the banner on. The texts are placeholders until the lawyer's
 * arrive (issue #40); `LegalDocuments::DRAFT` on the server is the same switch,
 * and `release:check` warns while it is on.
 */
export function LegalPage({
  title,
  updated,
  draft,
  children,
}: {
  title: string;
  updated: string;
  draft: boolean;
  children: ReactNode;
}) {
  const { authenticated } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {!authenticated && <PublicHeader action="both" />}

      <article className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <FileText className="h-7 w-7 text-primary" />
          {title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">Atnaujinta {updated}</p>

        {draft && (
          <p className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            Tai laikinas tekstas. Galutinę redakciją rengia teisininkė — kai ji bus
            paruošta, šis tekstas bus pakeistas ir apie tai pranešime.
          </p>
        )}

        <div className="mt-6 space-y-6 text-[15px] leading-relaxed [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:space-y-1">
          {children}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t pt-4 text-sm text-muted-foreground">
          <LegalLinks />
          <Link to="/duk" className="underline underline-offset-4">
            Dažni klausimai
          </Link>
        </div>
      </article>
    </div>
  );
}
